export const SCREEN0_BUNDLE = "com.screen0";
/** English Screen Time grouped label — web aliases map this to `productivity`. */
export const SCREEN0_CATEGORY = "Productivity & Finance";

export type CanonicalCategoryKey =
  | "books"
  | "business"
  | "catalogs"
  | "developerTools"
  | "education"
  | "entertainment"
  | "finance"
  | "foodAndDrink"
  | "games"
  | "graphicsAndDesign"
  | "healthAndFitness"
  | "kids"
  | "lifestyle"
  | "medical"
  | "miscellaneous"
  | "music"
  | "navigation"
  | "news"
  | "newsstand"
  | "photoAndVideo"
  | "productivity"
  | "reference"
  | "shopping"
  | "socialNetworking"
  | "sports"
  | "stickers"
  | "travel"
  | "system"
  | "utilities"
  | "weather"
  | "uncategorized"
  | "other"
  | "website";

const LABEL_ALIASES: Record<string, CanonicalCategoryKey> = {
  books: "books",
  business: "business",
  catalogs: "catalogs",
  developertools: "developerTools",
  education: "education",
  entertainment: "entertainment",
  finance: "finance",
  foodanddrink: "foodAndDrink",
  games: "games",
  graphicsanddesign: "graphicsAndDesign",
  healthandfitness: "healthAndFitness",
  kids: "kids",
  lifestyle: "lifestyle",
  medical: "medical",
  miscellaneous: "miscellaneous",
  music: "music",
  navigation: "navigation",
  news: "news",
  newsstand: "newsstand",
  photoandvideo: "photoAndVideo",
  productivity: "productivity",
  reference: "reference",
  shopping: "shopping",
  socialnetworking: "socialNetworking",
  sports: "sports",
  stickers: "stickers",
  travel: "travel",
  utilities: "utilities",
  weather: "weather",
  "developer tools": "developerTools",
  "food & drink": "foodAndDrink",
  "food and drink": "foodAndDrink",
  "graphics & design": "graphicsAndDesign",
  "graphics and design": "graphicsAndDesign",
  "health & fitness": "healthAndFitness",
  "health and fitness": "healthAndFitness",
  "photo & video": "photoAndVideo",
  "photo and video": "photoAndVideo",
  "social networking": "socialNetworking",
  social: "socialNetworking",
  "social network": "socialNetworking",
  uncategorized: "other",
  other: "other",
  website: "website",
  browser: "website",
  "productivity and finance": "productivity",
  "productivity & finance": "productivity",
  "finance and productivity": "productivity",
  "finance & productivity": "productivity",
  system: "system",
  "shopping and food": "shopping",
  "shopping & food": "shopping",
  "food and shopping": "shopping",
  "food & shopping": "shopping",
  creativity: "graphicsAndDesign",
  "information and reading": "news",
  "information & reading": "news",
  "reading and information": "reference",
  "reading & information": "reference",
  "non categorizzato": "other",
  "senza categoria": "other",
  intrattenimento: "entertainment",
  finanza: "finance",
  giochi: "games",
  notizie: "news",
  musica: "music",
  viaggi: "travel",
  "salute e benessere": "healthAndFitness",
  "foto e video": "photoAndVideo",
  istruzione: "education",
  riferimenti: "reference",
  medicina: "medical",
  "stile di vita": "lifestyle",
  bambini: "kids",
  meteo: "weather",
  navigazione: "navigation",
  libri: "books",
  acquisti: "shopping",
  sport: "sports",
  affari: "business",
  "grafica e design": "graphicsAndDesign",
  "produttività": "productivity",
  "produttivitÃ ": "productivity",
  produttivita: "productivity",
  "utilità": "utilities",
  "utilitÃ ": "utilities",
  utilita: "utilities",
  // iOS Screen Time grouped labels — Italian
  svago: "entertainment",
  altro: "other",
  creatività: "graphicsAndDesign",
  creativita: "graphicsAndDesign",
  "informazione e lettura": "news",
  "produttività e finanza": "productivity",
  "produttivita e finanza": "productivity",
  sistema: "system",
  utility: "utilities",
  "acquisti e cibo": "shopping",
  // iOS Screen Time grouped labels — French
  divertissement: "entertainment",
  créativité: "graphicsAndDesign",
  creativite: "graphicsAndDesign",
  "information et lecture": "news",
  "productivité et finances": "productivity",
  "productivite et finances": "productivity",
  "achats et alimentation": "shopping",
  "santé et forme": "healthAndFitness",
  "sante et forme": "healthAndFitness",
  utilitaires: "utilities",
  système: "system",
  systeme: "system",
  autre: "other",
  jeux: "games",
  voyages: "travel",
  // iOS Screen Time grouped labels — German
  unterhaltung: "entertainment",
  kreativität: "graphicsAndDesign",
  kreativitat: "graphicsAndDesign",
  "informationen und lesen": "news",
  "produktivität und finanzen": "productivity",
  "produktivitat und finanzen": "productivity",
  "einkaufen und essen": "shopping",
  "gesundheit und fitness": "healthAndFitness",
  dienstprogramme: "utilities",
  sonstiges: "other",
  spiele: "games",
  reisen: "travel",
  soziales: "socialNetworking",
  // iOS Screen Time grouped labels — Spanish
  entretenimiento: "entertainment",
  ocio: "entertainment",
  creatividad: "graphicsAndDesign",
  "información y lectura": "news",
  "informacion y lectura": "news",
  "productividad y finanzas": "productivity",
  "compras y comida": "shopping",
  "salud y estado físico": "healthAndFitness",
  "salud y estado fisico": "healthAndFitness",
  utilidades: "utilities",
  otros: "other",
  juegos: "games",
  viajes: "travel",
};

/** iOS Screen Time grouped category labels shown in Settings and Today category mode. */
export const IOS_SCREEN_TIME_CATEGORY_LABELS = [
  "Social",
  "Games",
  "Entertainment",
  "Creativity",
  "Education",
  "Health & Fitness",
  "Information & Reading",
  "Productivity & Finance",
  "Shopping & Food",
  "Travel",
  "Utilities",
  "System",
  "Other",
] as const;

export function normalizeCategoryLabel(label: string): CanonicalCategoryKey | null {
  if (!label) return null;
  return LABEL_ALIASES[label.trim().toLowerCase()] ?? null;
}

/** Map any incoming category label to a stable canonical key; unknown labels become `other`. */
export function resolveCanonicalCategoryKey(category?: string | null): CanonicalCategoryKey {
  const trimmed = category?.trim() ?? "";
  if (!trimmed) return "other";
  return normalizeCategoryLabel(trimmed) ?? "other";
}

/** Collapse empty, Uncategorized, and Other variants into the canonical `other` bucket. */
export function normalizeCategoryBucket(category?: string | null): CanonicalCategoryKey {
  return resolveCanonicalCategoryKey(category);
}

export function isFallbackAppCategory(category?: string | null): boolean {
  return resolveCanonicalCategoryKey(category) === "other";
}

/** Force Screen0 to Productivity & Finance; pass through other apps unchanged. */
export function resolveDisplayCategory(
  appIdentifier?: string | null,
  category?: string | null,
): string | undefined {
  if (appIdentifier?.trim() === SCREEN0_BUNDLE) return SCREEN0_CATEGORY;
  const trimmed = category?.trim();
  return trimmed || undefined;
}

export function resolveAppCategory(
  current?: string | null,
  next?: string | null,
  appIdentifier?: string | null,
): string | undefined {
  const override = resolveDisplayCategory(appIdentifier, undefined);
  if (override) return override;

  const currentTrimmed = current?.trim();
  const nextTrimmed = next?.trim();
  const currentIsReal = Boolean(currentTrimmed) && !isFallbackAppCategory(currentTrimmed);
  const nextIsReal = Boolean(nextTrimmed) && !isFallbackAppCategory(nextTrimmed);

  if (nextIsReal) return nextTrimmed;
  if (currentIsReal) return currentTrimmed;
  return currentTrimmed || nextTrimmed || undefined;
}
